import React, { useState, useEffect, useCallback, useContext } from 'react';
import { AuthContext } from '../context/authContext';
import ModalVisita from '../components/ModalVisita';
import CalendarioVisitas from '../components/CalendarioVisitas';
import { colorVisitaPorArea } from '../constants/config';

// Formatea "10:00:00" -> "10:00" y "YYYY-MM-DD" -> "dd/mm/yyyy"
const formatFecha = (fecha) => {
    if (!fecha) return '-';
    const [y, m, d] = String(fecha).split('T')[0].split('-');
    if (!y || !m || !d) return '-';
    return `${d}/${m}/${y}`;
};
const formatHora = (hora) => {
    if (!hora) return '-';
    return String(hora).slice(0, 5);
};

const CalendarioPage = () => {
    const { role, token } = useContext(AuthContext);
    const [visitas, setVisitas] = useState([]);
    const [obras, setObras] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [modalOpen, setModalOpen] = useState(false);
    const [fechaModal, setFechaModal] = useState(null);
    const [obraModal, setObraModal] = useState('');
    const [visitaEditable, setVisitaEditable] = useState(null);
    const [detalleColapsado, setDetalleColapsado] = useState(true);

    const headers = {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };

    const fetchVisitas = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const res = await fetch('/api/visitas', { headers });
            if (!res.ok) throw new Error('Error al cargar visitas');
            const data = await res.json();
            setVisitas(data);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [token]);

    const fetchObras = useCallback(async () => {
        try {
            const res = await fetch('/api/obras_inicio?refresh=1', { headers });
            if (!res.ok) throw new Error('Error al cargar obras');
            const data = await res.json();
            const lista = Array.isArray(data) ? data : (data.obras || []);
            setObras(lista.map(o => ({
                id: o.id,
                id_partido_obra: o.id_partido_obra,
                nombre_obra: o.nombre_obra || '',
                area_responsable: o.area_responsable || '',
                label: `${o.id_partido_obra} — ${o.nombre_obra || ''}`.trim(),
            })).filter(o => o.id));
        } catch (err) {
            console.error(err);
        }
    }, [token]);

    useEffect(() => {
        fetchVisitas();
        fetchObras();
    }, [fetchVisitas, fetchObras]);

    const abrirModal = (fecha, obraId) => {
        setVisitaEditable(null);
        setFechaModal(fecha);
        setObraModal(obraId || '');
        setModalOpen(true);
    };

    const abrirEdicion = (visita) => {
        setVisitaEditable(visita);
        setFechaModal(null);
        setObraModal(visita.id_tabla_obras || '');
        setModalOpen(true);
    };

    const eliminar = async (id) => {
        if (!window.confirm('¿Seguro que desea eliminar esta visita?')) return;
        try {
            const res = await fetch(`/api/visitas/${id}`, {
                method: 'DELETE',
                headers,
            });
            if (!res.ok) throw new Error('Error al eliminar');
            fetchVisitas();
        } catch (err) {
            console.error(err);
        }
    };

    return (
        <div className="p-6 w-full max-w-full">
            <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
                <h1 className="text-xl font-bold text-gray-800 dark:text-gray-100">Calendario de Visitas</h1>
            </div>

            {error && <p className="text-red-600 mb-4">{error}</p>}
            {loading && <p className="text-gray-500 mb-4">Cargando visitas...</p>}

            <CalendarioVisitas
                visitas={visitas}
                obras={obras}
                onDobleClickFecha={(fecha) => abrirModal(fecha, '')}
                onDobleClickVisita={(visita) => abrirEdicion(visita)}
            />

            {role === 'admin' && (
            <div className="mt-4 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                <button
                    onClick={() => setDetalleColapsado(!detalleColapsado)}
                    className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 dark:bg-gray-900 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                >
                    <span>Detalle de visitas ({visitas.length})</span>
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                        {detalleColapsado ? '▲ Mostrar' : '▼ Ocultar'}
                    </span>
                </button>

                {!detalleColapsado && (
                    <div className="p-4 bg-white dark:bg-gray-800">
                        {visitas.length === 0 && (
                            <p className="text-gray-500">No hay visitas agendadas.</p>
                        )}

                        {visitas.length > 0 && (
                            <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                                    <thead className="bg-gray-50 dark:bg-gray-900">
                                        <tr>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Obra</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Fecha</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Hora</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Motivo</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Observaciones</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Personal asignado</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Acciones</th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                                        {visitas.map(v => (
                                            <tr key={v.id}>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                                                    {v.id_partido_obra ? `${v.id_partido_obra} — ${v.nombre_obra}` : colorVisitaPorArea(v.area_responsable).label}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">{formatFecha(v.fecha)}{v.fecha_hasta ? ` → ${formatFecha(v.fecha_hasta)}` : ''}</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">{formatHora(v.hora)}</td>
                                                <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-300">{Array.isArray(v.motivo) ? v.motivo.join(', ') : (v.motivo || '-')}</td>
                                                <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-300">{v.observaciones || '-'}</td>
                                                <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-300">
                                                    {v.personal?.length ? v.personal.map(p => p.name).join(', ') : '-'}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm">
                                                    <div className="flex gap-2">
                                                        <button
                                                            onClick={() => abrirEdicion(v)}
                                                            className="px-3 py-1 text-xs bg-blue-600 hover:bg-blue-700 text-white rounded-md transition-colors"
                                                        >
                                                            Editar
                                                        </button>
                                                        <button
                                                            onClick={() => eliminar(v.id)}
                                                            className="px-3 py-1 text-xs bg-red-600 hover:bg-red-700 text-white rounded-md transition-colors"
                                                        >
                                                            Eliminar
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                )}
            </div>
            )}

            <ModalVisita
                isOpen={modalOpen}
                onClose={() => setModalOpen(false)}
                token={token}
                id_tabla_obras={obraModal}
                obraLabel={obraModal ? obras.find(o => String(o.id) === String(obraModal))?.label : ''}
                fechaInicial={fechaModal}
                opcionesObras={obras}
                visitaEditable={visitaEditable}
                onGuardada={fetchVisitas}
            />
        </div>
    );
};

export default CalendarioPage;